import type { SxProps, Theme } from "@mui/material"
import type { ScheduledWorkflowDeliverableListItem, ScheduledWorkflowSchedule } from "resources/scheduled-workflows/types"
import { formatSchedule } from "./schedule-format"

export const UNREAD_DOT_SX = { width: 8, height: 8, borderRadius: "50%", bgcolor: "primary.main" } satisfies SxProps<Theme>

export function reportEntryLabel(deliverable: Pick<ScheduledWorkflowDeliverableListItem, "workflowName" | "createdAt">, viewerZone: string): string {
  const createdAt = new Date(deliverable.createdAt)
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: viewerZone, weekday: "short", month: "short", day: "numeric" }).formatToParts(createdAt)
  const partValue = (type: string) => parts.find((part) => part.type === type)?.value
  const clockTime = new Intl.DateTimeFormat("en-US", { timeZone: viewerZone, hour: "numeric", minute: "2-digit" }).format(createdAt)
  return `${deliverable.workflowName} — ${partValue("weekday")} ${partValue("month")} ${partValue("day")}, ${clockTime}`
}

interface ReportSourceLine {
  before: string
  workflowName: string
  after: string
}

export type ReportAccess = { kind: "owner" } | { kind: "sharee", ownerName: string | null } | { kind: "unknown" }

interface ReportOwnership {
  ownerUserId?: string | null
  ownerName?: string | null
}

const OWNER_ACCESS: ReportAccess = { kind: "owner" }
const UNKNOWN_ACCESS: ReportAccess = { kind: "unknown" }

export function reportAccessOf(report: ReportOwnership, viewerUserId: string | undefined): ReportAccess {
  if (report.ownerUserId == null || viewerUserId == null) return UNKNOWN_ACCESS
  if (report.ownerUserId === viewerUserId) return OWNER_ACCESS
  return { kind: "sharee", ownerName: report.ownerName ?? null }
}

export function sharedReportMarker(access: ReportAccess): string | undefined {
  if (access.kind !== "sharee") return undefined
  return access.ownerName == null ? "Shared with you" : `Shared by ${access.ownerName}`
}

function sourcePrefix(access: ReportAccess): string {
  if (access.kind === "owner") return "From your "
  if (access.kind === "unknown") return "From the "
  return access.ownerName == null ? "From a shared " : `From ${access.ownerName}'s `
}

export function reportSourceLine(workflowName: string, schedule: ScheduledWorkflowSchedule, viewerZone: string, now: Date, access: ReportAccess): ReportSourceLine {
  const base = { before: sourcePrefix(access), workflowName, after: " workflow" }
  return schedule == null ? base : { ...base, after: `${base.after} · ${formatSchedule(schedule, viewerZone, { now })}` }
}
